import { defineStore } from 'pinia';
import { raisesBadge, staysInInbox } from '@/components/notifications/presenter';

/**
 * App store - manages application-level state
 * Replaces various window globals (AppTitle, etc.)
 */
export const useAppStore = defineStore('app', {
    state: () => ({
        title: '',
        defaultTitle: '',
        loading: false,
        sidebarOpen: true,
        notifications: [],
        notificationTypes: {},
        notificationTypesLoaded: false,
        unreadByOrganization: {},
        config: {},
        // Bumped when an action taken outside a record's own page (e.g. adding a
        // collaborator from the notification bell) should make that record's open
        // preview refetch. BasePreview watches this and reloads when it matches.
        recordRefresh: null
    }),

    getters: {
        /**
         * Get the current page title
         */
        pageTitle: state => {
            return state.title || state.defaultTitle;
        },

        /**
         * Check if there are any notifications
         */
        hasNotifications: state => {
            return state.notifications.length > 0;
        },

        /**
         * Badge counter: unread notifications that deserve attention.
         * Informational activity stays in the list but never raises the badge.
         */
        unreadCount: state => {
            return state.notifications.filter(n => !n.read && raisesBadge(n)).length;
        },

        /**
         * Every unread notification, informational ones included.
         */
        unreadTotal: state => {
            return state.notifications.filter(n => !n.read).length;
        },

        /**
         * Get config value
         */
        getConfig: state => {
            return (key, defaultValue = null) => {
                return state.config[key] ?? defaultValue;
            };
        }
    },

    actions: {
        /**
         * Initialize store from window globals (for backward compatibility)
         */
        initFromLegacy() {
            if (typeof window !== 'undefined') {
                this.defaultTitle = window.AppTitle || document.title;
                this.title = this.defaultTitle;
                if (window.config) {
                    this.config = window.config;
                }
            }
        },

        /**
         * Set the page title
         * @param {String} title - Page title
         */
        setTitle(title) {
            this.title = title;
            document.title = title ? `${title} - ${this.defaultTitle}` : this.defaultTitle;
        },

        /**
         * Reset to default title
         */
        resetTitle() {
            this.title = this.defaultTitle;
            document.title = this.defaultTitle;
        },

        /**
         * Set loading state
         * @param {Boolean} loading - Loading state
         */
        setLoading(loading) {
            this.loading = loading;
        },

        /**
         * Toggle sidebar
         */
        toggleSideBar() {
            this.sidebarOpen = !this.sidebarOpen;
        },

        /**
         * Set sidebar state
         * @param {Boolean} open - SideBar open state
         */
        setSideBar(open) {
            this.sidebarOpen = open;
        },

        /**
         * Load notifications from server
         */
        async loadNotifications() {
            try {
                const response = await axios.get('/api/notifications');
                this.notifications = response.data.data || [];
                this.unreadByOrganization = response.data.unread_by_organization || {};
            } catch (e) {
                // Silently fail — notifications are non-critical
            }
        },

        /**
         * Load how each notification type is rendered (translation key, target
         * record, category). Served by the backend registry and cached for the
         * session — the catalog only changes with a deployment.
         */
        async loadNotificationTypes() {
            if (this.notificationTypesLoaded) {
                return;
            }
            try {
                const response = await axios.get('/api/notifications/types');
                this.notificationTypes = response.data.data || {};
                this.notificationTypesLoaded = true;
            } catch (e) {
                // Silently fail — the bell falls back to raw payload text
            }
        },

        /**
         * Add a notification received over broadcast, whose payload is flat.
         * Normalized to the same shape the API returns so both sources render
         * through one path.
         *
         * A transient type is normalized but never stored: it has no row on the
         * server either, and the caller still needs the object to raise its
         * toast. Returns it so the bell does not have to read `notifications[0]`
         * back and hope it is the one that just arrived.
         *
         * @param {Object} payload - Broadcast payload
         * @returns {Object} The normalized notification
         */
        addNotification(payload) {
            const { id, type, type_key, organization_id, priority, ...data } = payload;

            const notification = {
                id,
                type,
                type_key,
                organization_id,
                priority,
                data,
                read: false,
                created_at: new Date().toISOString()
            };

            if (staysInInbox(notification)) {
                this.notifications.unshift(notification);
            }

            return notification;
        },

        /**
         * Mark notification as read
         * @param {String} id - Notification ID
         */
        async markAsRead(id) {
            const notification = this.notifications.find(n => n.id === id);
            if (notification) {
                notification.read = true;
            }
            try {
                await axios.post(`/api/notifications/${id}/read`);
            } catch (e) {}
        },

        /**
         * Mark all notifications as read
         */
        async markAllAsRead(organizationId = null) {
            this.notifications
                .filter(n => !organizationId || n.organization_id === organizationId)
                .forEach(n => {
                    n.read = true;
                });
            try {
                await axios.post(
                    '/api/notifications/read-all',
                    organizationId ? { organization_id: organizationId } : {}
                );
            } catch (e) {}
        },

        /**
         * Hide a notification from the inbox. The row is archived server-side,
         * never deleted — an ERP has to keep track of what it told whom.
         * @param {String} id - Notification ID
         */
        async removeNotification(id) {
            this.notifications = this.notifications.filter(n => n.id !== id);
            try {
                await axios.delete(`/api/notifications/${id}`);
            } catch (e) {}
        },

        /**
         * Empty the inbox, read and unread alike. Rows are archived server-side,
         * never deleted, exactly like dismissing a single one.
         * @param {Number|null} organizationId - Limit to one organization
         */
        async clearNotifications(organizationId = null) {
            try {
                await axios.post(
                    '/api/notifications/clear-all',
                    organizationId ? { organization_id: organizationId } : {}
                );
            } catch (e) {}
            // Refetched rather than filtered locally: the server also clears the
            // platform notifications the inbox lists alongside the organization's.
            await this.loadNotifications();
        },

        /**
         * Ask an open record preview to refetch itself.
         * @param {String} type - Entity type (e.g. 'contact')
         * @param {Number|String} id - Record id
         */
        signalRecordRefresh(type, id) {
            this.recordRefresh = { type, id: String(id), at: Date.now() };
        },

        /**
         * Set config
         * @param {Object} config - Configuration object
         */
        setConfig(config) {
            this.config = config;
        }
    }
});
